import { useEffect, useState } from 'react';

import { type IntlShape, type MessageDescriptor, useIntl } from 'react-intl-next';

import { fg } from '@atlaskit/platform-feature-flags';
import {
	type ConfluenceRangeFilter,
	jiraIssueTypeJQLQuery,
	type SearchConfluenceDocumentStatus,
	type SearchContainerStatus,
	type SearchIssueStatusCategory,
	type SearchJiraProductType,
} from '@atlassian/search-client';

import {
	type AllFilterStates,
	CUSTOM_OPTION_VALUE,
} from '../../../../common/constants/filters/types';
import { type TypeFilterValueKey } from '../../../../common/constants/filters/universal-type';
import { ProductKeys } from '../../../../common/constants/products';
import {
	convertCustomDateToRange,
	convertLastUpdatedToDateRange,
	getConfluenceDateOptionFromCustomDates,
	getConfluenceDateOptionValues,
} from '../../../../common/utils/filters';
import { isSelectFilter } from '../../base-select-filter/utils';

import { messages } from './messages';

type SearchStatusKeys = 'showCurrent' | 'showArchived';

export const ContainerStatusKeys: {
	[K in SearchStatusKeys]: SearchContainerStatus[];
} = {
	['showCurrent']: [],
	['showArchived']: ['ARCHIVED', 'CURRENT'],
};

export const ContentStatusKeys: {
	[K in SearchStatusKeys]: SearchConfluenceDocumentStatus[];
} = {
	['showCurrent']: [],
	['showArchived']: ['ARCHIVED', 'CURRENT', 'DRAFT'],
};

export const getContentStatusValue = (filter?: AllFilterStates) => {
	const archivedContentOption = getBooleanFilterValue(filter);

	return archivedContentOption
		? ContentStatusKeys['showArchived']
		: ContentStatusKeys['showCurrent'];
};

export const getContainerStatusValue = (filter?: AllFilterStates) => {
	const archivedContentOption = getBooleanFilterValue(filter);

	return archivedContentOption
		? ContainerStatusKeys['showArchived']
		: ContainerStatusKeys['showCurrent'];
};

export const getArchivedAndPendingComponentsValue = (filter?: AllFilterStates) => {
	const archivedContentOption = getBooleanFilterValue(filter);

	return archivedContentOption ? ['ACTIVE', 'PENDING', 'ARCHIVED'] : [];
};

type JiraProductKeys =
	| typeof ProductKeys.Jira
	| typeof ProductKeys.JiraProductDiscovery
	| typeof ProductKeys.JiraServiceManagement;

export const JiraProductProjectFilterKey: {
	[K in JiraProductKeys]: SearchJiraProductType[];
} = {
	[ProductKeys.Jira]: ['software', 'business'],
	[ProductKeys.JiraProductDiscovery]: ['product_discovery'],
	[ProductKeys.JiraServiceManagement]: ['service_desk'],
};

export const omitNonJiraFilterValues = <T,>(
	filterValues: T[],
	selectedProduct?: ProductKeys,
): T[] => {
	// when all products are searched
	if (!selectedProduct) {
		// don't return any values in all product selected mode for assignee and project filters
		return [];
	}

	switch (selectedProduct) {
		case ProductKeys.Jira:
		case ProductKeys.JiraProductDiscovery:
		case ProductKeys.JiraServiceManagement:
			return filterValues;
	}

	return [];
};

// providing dummy value when no results returned so that it still filters on the type
// otherwise an empty array would return all types and not match what the user filters
export const DUMMY_JIRA_ISSUE_TYPE_ID = 'DUMMY_JIRA_ISSUE_TYPE_ID';

export const getJiraIssueTypesFilter = async (
	cloudId: string | undefined,
	intl: IntlShape,
	issueType: Partial<TypeFilterValueKey>,
): Promise<string> => {
	if (!cloudId) {
		return DUMMY_JIRA_ISSUE_TYPE_ID;
	}
	const searchString = intl.formatMessage(messages[issueType] as MessageDescriptor);
	const issueTypes = await jiraIssueTypeJQLQuery(cloudId, searchString);

	const ids = issueTypes?.data?.jira.jqlBuilder.fieldValues.edges;
	if (!ids || ids.length === 0) {
		return DUMMY_JIRA_ISSUE_TYPE_ID;
	}
	if (ids.length > 1) {
		const issueTypeNode = ids.find((id) => id.node?.displayName === searchString);
		if (!issueTypeNode) {
			return DUMMY_JIRA_ISSUE_TYPE_ID;
		}
		const issueType = issueTypeNode.node?.issueTypes[0];
		return issueType && issueType.issueTypeId ? issueType.issueTypeId : DUMMY_JIRA_ISSUE_TYPE_ID;
	} else {
		const node = ids[0].node;
		if (!node) {
			return DUMMY_JIRA_ISSUE_TYPE_ID;
		}
		return node.issueTypes[0].issueTypeId || DUMMY_JIRA_ISSUE_TYPE_ID;
	}
};

// TODO: QS-6991 Clean this up when removing search-page-relay-useselectedfilters as it is now implemented via <FetchJsmIncidentFilterIds />
export const useJiraIssueTypeFilters = (
	cloudId: string | undefined,
	selectedEntities: string[],
) => {
	const intl = useIntl();
	const [jiraIssueTypeFilters, setJiraIssueTypeFilters] = useState<string[]>([]);

	useEffect(() => {
		const getJiraIssues = async () => {
			if (selectedEntities.includes('incident')) {
				const issueType = await getJiraIssueTypesFilter(cloudId, intl, 'incident');
				setJiraIssueTypeFilters([issueType]);
			} else {
				setJiraIssueTypeFilters([]);
			}
		};
		getJiraIssues();
	}, [selectedEntities, cloudId, intl]);

	return jiraIssueTypeFilters;
};

export const getSelectFilterValue = (filter?: AllFilterStates): string[] => {
	return isSelectFilter(filter?.type) ? filter.value : [];
};
export const getSelectFilterCustomOptionValue = (filter?: AllFilterStates) => {
	return isSelectFilter(filter?.type) ? filter.customValue : {};
};

export const getBooleanFilterValue = (filter?: AllFilterStates) => {
	return filter?.type === 'boolean' ? filter.value : false;
};

export const getSelectedDateRange = (filter?: AllFilterStates) => {
	const selectedDateOption = getSelectFilterValue(filter)[0];
	if (selectedDateOption && selectedDateOption !== CUSTOM_OPTION_VALUE) {
		return convertLastUpdatedToDateRange(selectedDateOption);
	} else if (selectedDateOption === CUSTOM_OPTION_VALUE) {
		const customDateRange = getSelectFilterCustomOptionValue(filter);
		if (customDateRange?.from || customDateRange?.to) {
			return convertCustomDateToRange(customDateRange.from, customDateRange.to);
		}
	}
	return;
};

export const getFilterDateRange = (filter?: AllFilterStates): ConfluenceRangeFilter[] => {
	const selectedDateOption = getSelectFilterValue(filter)[0];

	if (selectedDateOption && selectedDateOption !== CUSTOM_OPTION_VALUE) {
		return [getConfluenceDateOptionValues(selectedDateOption)];
	} else if (selectedDateOption === CUSTOM_OPTION_VALUE) {
		const customDateRange = getSelectFilterCustomOptionValue(filter);
		if (customDateRange?.from && customDateRange?.to) {
			return [getConfluenceDateOptionFromCustomDates(customDateRange.from, customDateRange.to)];
		}
	}
	return [];
};

export const getJiraProjectFilter = (selectedProduct?: ProductKeys): SearchJiraProductType[] => {
	if (!selectedProduct) {
		return []; // In the BE, it will target all Jira products if the array is empty
	}

	switch (selectedProduct) {
		case ProductKeys.Jira:
		case ProductKeys.JiraProductDiscovery:
		case ProductKeys.JiraServiceManagement:
			return JiraProductProjectFilterKey[selectedProduct];
	}

	return [];
};

// Only use the following values for Jira issue statuses or else it will be ignored in
// the GraphQL query request.
export const allowedJiraIssueStatuses: SearchIssueStatusCategory[] = [
	'OPEN',
	'NEW',
	'INDETERMINATE',
	'DONE',
	'UNDEFINED',
];

export const getJiraIssueStatusFilterOptions = (
	filter?: AllFilterStates,
): SearchIssueStatusCategory[] => {
	const filterValues = getSelectFilterValue(filter);
	const appliedFilterOptions = filterValues.flatMap((filtervalue) => {
		const filterOptions = allowedJiraIssueStatuses
			.filter((status) => {
				if (!fg('rovo_search_in_jira_granular_status_filters')) {
					return status === 'OPEN' || status === 'DONE';
				} else {
					return status !== 'OPEN';
				}
			})
			.find((allowedValue) => filtervalue === allowedValue);
		return filterOptions ? [filterOptions] : [];
	});
	return appliedFilterOptions;
};
